/**
 * Session Redux Slice
 *
 * Manages authentication state including login, 2FA, phone registration,
 * token refresh, and logout.
 */

import {
  createSlice,
  createAsyncThunk,
  isPending,
  isFulfilled,
  isRejected,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { SessionState } from "@/features/auth/types/auth.types";
import { queryClient } from "@/shared/lib/queryClient";
import { OrganizationService } from "@/shared/services/organization.service";
import { getCurrentUser } from "@/shared/services/user.service";
import type { ErrorResponse, OrganizationPlan } from "@/shared/types/common";
import type { Representative } from "@/shared/types/representative.types";
import type { RepresentativePermissions } from "@/shared/types/representativePermissions";
import { clearPersistedFilters } from "@/shared/utils/persistedFilters";
import { AUTH_TOASTS } from "../configs/constants";
import { deleteAuthInfo, publishLogout } from "../services/accessToken.service";
import {
  exchangeGrantLogin,
  impersonate,
  login,
  refreshToken,
  login2FA as verify2FAService,
  registerPhone as registerPhoneService,
  revokeToken,
} from "../services/auth.service";
import {
  isTwoFactorRequired,
  isMissingCellPhoneNumber,
  type AuthErrorWithoutCellPhone,
  type AuthWith2FactorResponse,
} from "../types/auth.types";

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: SessionState = {
  toast: null,
  isAuthenticating: false,
  isDoubleAuthRequired: false,
  registerPhone: false,
  cell_phone: "",
  isAuthenticated: false,
  user: null,
  organizationPlan: null,
  isFetchingOrganizationPlan: false,
  representativePermissions: null,
  error: null,
  channel: "",
  otp_challenge: null,
  message: null,
  description: null,
  grant_type: undefined,
  username: null,
  password: null,
  scope: undefined,
  email: "",
  twoFaTimestamp: undefined,
};

// =============================================================================
// STATE CHANGE HELPERS
// =============================================================================

const changeStateOnMissingCellPhone = (
  payload: { email: string, password: string },
  auth: AuthErrorWithoutCellPhone,
): SessionState => {
  return {
    ...initialState,
    grant_type: "password",
    username: payload.email,
    password: payload.password,
    scope: "representative",
    registerPhone: true,
    description: auth.error.description,
    toast: AUTH_TOASTS.PHONE_REQUIRED,
  };
};

const changeStateOnTwoFactorRequired = (
  payload: { email: string, password: string },
  auth: AuthWith2FactorResponse,
): SessionState => {
  return {
    ...initialState,
    isDoubleAuthRequired: true,
    grant_type: "password",
    username: payload.email,
    password: payload.password,
    scope: "representative",
    description: auth.description ?? null,
    otp_challenge: auth.challenge,
    channel: auth.channel,
    message: auth.message,
    twoFaTimestamp: auth.code_sent_at,
    toast: AUTH_TOASTS.CODE_SENT,
  };
};

const changeStateOnAuthSucceed = (
  user: SessionState["user"],
  organizationPlan: OrganizationPlan | null = null,
  representativePermissions: RepresentativePermissions | null = null,
): SessionState => {
  return {
    ...initialState,
    isAuthenticated: true,
    user,
    organizationPlan,
    representativePermissions,
    toast: AUTH_TOASTS.LOGIN_SUCCESS,
  };
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

type OrganizationPlanData = {
  plan: OrganizationPlan | null
  representativePermissions: RepresentativePermissions | null
};

const fetchOrganizationPlan = async (
  organizationId: string,
): Promise<OrganizationPlanData> => {
  try {
    const response = await OrganizationService.getOrganizationPlan(organizationId);
    return {
      plan: response.plan,
      representativePermissions: response.plan.representative_permissions ?? null,
    };
  }
  catch (_) {
    return { plan: null, representativePermissions: null };
  }
};

const onAuthSucceed = async (): Promise<{
  user: SessionState["user"]
  organizationPlan: OrganizationPlan | null
  representativePermissions: RepresentativePermissions | null
}> => {
  queryClient.clear();

  const userRes = await getCurrentUser();

  const organizationId = userRes.data.representative?.organization_id;

  if (organizationId) {
    const { plan, representativePermissions } = await fetchOrganizationPlan(organizationId);
    return {
      user: userRes.data,
      organizationPlan: plan,
      representativePermissions,
    };
  }

  return {
    user: userRes.data,
    organizationPlan: null,
    representativePermissions: null,
  };
};

// =============================================================================
// ASYNC THUNKS
// =============================================================================

/**
 * Login user with email/password/captcha
 */
export const loginUser = createAsyncThunk<
  SessionState,
  { email: string, password: string, captchaToken?: string }
>("session/loginUser", async ({ email, password, captchaToken }, { rejectWithValue }) => {
  try {
    const auth = await login({
      email,
      password,
      captchaToken: captchaToken ?? "",
    });

    if (isMissingCellPhoneNumber(auth)) {
      return changeStateOnMissingCellPhone({ email, password }, auth);
    }

    if (isTwoFactorRequired(auth)) {
      return changeStateOnTwoFactorRequired({ email, password }, auth);
    }

    const { user, organizationPlan, representativePermissions } = await onAuthSucceed();
    return changeStateOnAuthSucceed(user, organizationPlan, representativePermissions);
  }
  catch (error: unknown) {
    return rejectWithValue({
      ...initialState,
      error: error as ErrorResponse,
      toast: null,
    });
  }
});

/**
 * Verify 2FA code
 */
export const login2FA = createAsyncThunk<SessionState, { otp_code: string }>(
  "session/login2FA",
  async ({ otp_code }, { rejectWithValue, getState }) => {
    const state = (getState() as { session: SessionState }).session;
    try {
      const { username, password, otp_challenge } = state;

      if (!username || !password || !otp_challenge) {
        throw rejectWithValue("Veuillez fournir tous les champs requis pour la vérification 2FA.");
      }

      await verify2FAService({
        email: username,
        password,
        otp_challenge,
        otp_code,
      });

      const { user, organizationPlan, representativePermissions } = await onAuthSucceed();
      return changeStateOnAuthSucceed(user, organizationPlan, representativePermissions);
    }
    catch (error: unknown) {
      return rejectWithValue({
        ...state,
        toast: null,
        error: {
          code: "unknown_error",
          title: "Erreur",
          description: error instanceof Error ? error.message : "Une erreur est survenue",
        },
      });
    }
  },
);

/**
 * Register phone number for 2FA
 */
export const loginRegisterPhone = createAsyncThunk<SessionState, { cell_phone: string }>(
  "session/registerPhone",
  async ({ cell_phone }, { rejectWithValue, getState }) => {
    const state = (getState() as { session: SessionState }).session;
    try {
      const { username, password } = state;

      if (!username || !password || !cell_phone) {
        throw new Error("Une erreur est survenue lors de l'enregistrement du numéro de téléphone");
      }

      const auth = await registerPhoneService({
        email: username,
        password,
        cell_phone,
      });

      return {
        ...changeStateOnTwoFactorRequired({ email: username, password }, auth),
        toast: AUTH_TOASTS.PHONE_REGISTERED,
      };
    }
    catch (error: unknown) {
      return rejectWithValue({
        ...state,
        toast: null,
        error: {
          code: "unknown_error",
          title: "Erreur",
          description: error instanceof Error ? error.message : "Une erreur est survenue",
        },
      });
    }
  },
);

/**
 * Admin impersonation
 */
export const loginImpersonate = createAsyncThunk<
  SessionState,
  { organizationId: string, token: string }
>("session/loginImpersonate", async ({ organizationId, token }, { rejectWithValue }) => {
  try {
    const auth = await impersonate({
      organizationId,
      token,
    });
    if (!auth) return rejectWithValue("Erreur lors de l'impersonation");

    const { user, organizationPlan, representativePermissions } = await onAuthSucceed();

    return {
      ...changeStateOnAuthSucceed(user, organizationPlan, representativePermissions),
      toast: AUTH_TOASTS.IMPERSONATION_SUCCESS,
    };
  }
  catch (error: unknown) {
    deleteAuthInfo();
    return rejectWithValue({
      ...initialState,
      isAuthenticated: false,
      error: {
        code: "unknown_error",
        title: "Erreur",
        description: error instanceof Error ? error.message : "Une erreur est survenue",
      },
    });
  }
});

/**
 * Grant-based impersonation via login token
 */
export const loginGrantExchange = createAsyncThunk<
  SessionState,
  { loginToken: string }
>("session/loginGrantExchange", async ({ loginToken }, { rejectWithValue }) => {
  try {
    const auth = await exchangeGrantLogin(loginToken);
    if (!auth) return rejectWithValue("Erreur lors de l'échange du token");

    const { user, organizationPlan, representativePermissions } = await onAuthSucceed();

    return {
      ...changeStateOnAuthSucceed(user, organizationPlan, representativePermissions),
      toast: AUTH_TOASTS.IMPERSONATION_SUCCESS,
    };
  }
  catch (error: unknown) {
    deleteAuthInfo();
    return rejectWithValue({
      ...initialState,
      isAuthenticated: false,
      error: {
        code: "unknown_error",
        title: "Erreur",
        description: error instanceof Error ? error.message : "Une erreur est survenue",
      },
    });
  }
});

/**
 * Restore the session: the access token is in memory only, so a new page gets one
 * through a refresh authenticated by the httpOnly refresh cookie
 */
export const refreshLogin = createAsyncThunk<SessionState, void>(
  "session/refreshLogin",
  async (_, { rejectWithValue }) => {
    try {
      const token = await refreshToken();
      const { user, organizationPlan, representativePermissions } = await onAuthSucceed();

      return {
        ...changeStateOnAuthSucceed(user, organizationPlan, representativePermissions),
        isAuthenticated: !!token,
      };
    }
    catch (error: unknown) {
      deleteAuthInfo();
      return rejectWithValue({
        ...initialState,
        error: {
          code: "unknown_error",
          title: "Erreur",
          description: error instanceof Error ? error.message : "Une erreur est survenue",
        },
      });
    }
  },
);

/**
 * Fetch organization plan
 */
export const fetchOrganizationPlanThunk = createAsyncThunk<OrganizationPlanData, string>(
  "session/fetchOrganizationPlan",
  async (organizationId, { rejectWithValue }) => {
    try {
      const data = await fetchOrganizationPlan(organizationId);
      if (!data.plan) throw new Error("Une erreur est survenue lors de la récupération du plan");
      return data;
    }
    catch (error: unknown) {
      return rejectWithValue({
        code: "plan_fetch_error",
        title: "Erreur",
        description: error instanceof Error
          ? error.message
          : "Une erreur est survenue lors de la récupération du plan",
      });
    }
  },
  {
    // Plusieurs composants montent useOrganizationPlan() en même temps au boot
    // et dispatchent ce thunk tant que le plan est absent. On déduplique les
    // requêtes concurrentes : si un fetch est déjà en vol, on ne relance pas.
    // `getState()` lit l'état live et `pending` passe le flag à true de façon
    // synchrone, donc le 2ᵉ dispatch du même tick est bien court-circuité.
    condition: (_organizationId, { getState }) =>
      !(getState() as { session: SessionState }).session.isFetchingOrganizationPlan,
  },
);

type LogoutOptions = {
  /** Chosen by the user: the return URL after login drops the filter params, unless the élu remembers them. */
  isVoluntary: boolean
  /**
   * Another tab logged out and told this one: its session is already revoked. Revoking
   * again would use the refresh cookie, which may by now belong to a new login.
   */
  fromOtherTab?: boolean
};

// =============================================================================
// SLICE DEFINITION
// =============================================================================

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    logout: (state, action: PayloadAction<LogoutOptions | undefined>): SessionState => {
      // YTI-1926 : l'élu garde ses filtres d'une session à l'autre, sauf s'il a
      // choisi de repartir des filtres par défaut à chaque connexion.
      const resetsFilters = state.user?.representative?.remember_filters === false;
      if (!action.payload?.fromOtherTab) {
        revokeToken();
        // The access token is in each tab's memory: the other tabs must drop theirs too.
        // Only from a signed-in tab, so that a failed session restore on the login page
        // does not sign out the tabs that are still connected.
        if (state.isAuthenticated) publishLogout(action.payload?.isVoluntary ?? false);
      }
      queryClient.clear();
      deleteAuthInfo();
      if (resetsFilters) clearPersistedFilters();
      return { ...initialState, hasResetFilters: resetsFilters && (action.payload?.isVoluntary ?? false) };
    },
    updateUser: (state, action) => {
      if (state.user) {
        state.user = action.payload;
      }
    },
    mergeSessionRepresentative: (state, action: PayloadAction<Partial<Representative>>) => {
      if (state.user) {
        state.user.representative = { ...state.user.representative, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationPlanThunk.pending, (state) => {
        state.isFetchingOrganizationPlan = true;
        if (state.error?.code === "plan_fetch_error") state.error = null;
      })
      .addCase(fetchOrganizationPlanThunk.fulfilled, (state, action) => {
        state.organizationPlan = action.payload.plan;
        state.representativePermissions = action.payload.representativePermissions;
        state.isFetchingOrganizationPlan = false;
      })
      .addCase(fetchOrganizationPlanThunk.rejected, (state, action) => {
        state.error = action.payload as ErrorResponse;
        state.isFetchingOrganizationPlan = false;
      })
      .addMatcher(
        isPending(loginUser, login2FA, loginRegisterPhone, loginImpersonate, loginGrantExchange, refreshLogin),
        (state) => ({
          ...state,
          isAuthenticating: true,
        }),
      )
      .addMatcher(
        isFulfilled(loginUser, login2FA, loginRegisterPhone, loginImpersonate, loginGrantExchange, refreshLogin),
        (state, action) => ({
          ...state,
          ...(action.payload),
          isAuthenticating: false,
        }),
      )
      .addMatcher(
        isRejected(loginUser, login2FA, loginRegisterPhone, loginImpersonate, loginGrantExchange, refreshLogin),
        (_state, action) => ({
          ...(action.payload as SessionState),
          isAuthenticating: false,
          isAuthenticated: false,
        }),
      );
  },
});

// =============================================================================
// EXPORTS
// =============================================================================

export const { logout, updateUser, mergeSessionRepresentative } = sessionSlice.actions;

// Selectors
export const selectOrganizationPlan = (state: { session: SessionState }) =>
  state.session.organizationPlan;
export const selectUser = (state: { session: SessionState }) =>
  state.session.user;
export const selectIsAuthenticated = (state: { session: SessionState }) =>
  state.session.isAuthenticated;
export const selectRepresentativePermissions = (state: { session: SessionState }) =>
  state.session.representativePermissions;

/**
 * Utility function to check if organization features should be fetched
 */
export const shouldFetchOrganizationPlan = (
  state: { session: SessionState },
): string | false => {
  const { isAuthenticated, user, organizationPlan } = state.session;

  if (isAuthenticated && user?.representative?.organization_id && !organizationPlan) {
    return user.representative.organization_id;
  }

  return false;
};

export default sessionSlice.reducer;
